import { ProviderType } from "@/types";
/** Front-end metadata for a provider */
export interface ProviderMeta {
  id: ProviderType;
  order?: number;
  name: string;
  description?: string;
  logoUrl: string;
  oauthUrl?: string;
  tokenUrl?: string;
  refreshUrl?: string;
  revokeUrl?: string;
  authType: "oauth" | "token" | "accessKeySecretKey";
  tokenName?: string;
  accessKeyName?: string;
  secretKeyName?: string;
  consoleUrl: string;
  tokenHelpUrl?: string;
  experimental?: boolean;
  compute?: boolean;
  /** User needs to confirm billing is setup before provisioning */
  confirmBilling?: boolean;
  getManageLink: (id: string) => string | undefined;
  getConsoleLink: (id: string) => string | undefined;
  summary: string[];
}

const providerMeta: Record<ProviderType, ProviderMeta> = {
  do: {
    id: "do",
    order: 1,
    name: "DigitalOcean",
    authType: "oauth",
    confirmBilling: true,
    compute: true,
    oauthUrl: "https://cloud.digitalocean.com/v1/oauth/authorize",
    tokenUrl: "https://cloud.digitalocean.com/v1/oauth/token",
    refreshUrl: "https://cloud.digitalocean.com/v1/oauth/token",
    revokeUrl: "https://cloud.digitalocean.com/v1/oauth/revoke",
    logoUrl: "/assets/provider/do-logo.svg",
    consoleUrl: "https://cloud.digitalocean.com/droplets",
    getManageLink: (id: string) => `https://cloud.digitalocean.com/droplets/${id}`,
    getConsoleLink: (id: string) =>
      `https://cloud.digitalocean.com/droplets/${id}/terminal/ui/?os_user=root`,
    summary: ["Easy setup process", "Optimized for DollarDeploy", "$200 credit for new users"]
  },
  hetzner: {
    id: "hetzner",
    order: 2,
    name: "Hetzner",
    logoUrl:
      "https://user-images.githubusercontent.com/1233275/187983134-17b69812-f4a6-4359-969c-5384ee296ecf.png",
    authType: "token",
    compute: true,
    consoleUrl: "https://console.hetzner.cloud/projects",
    tokenHelpUrl: "https://docs.hetzner.com/cloud/api/getting-started/generating-api-token/",
    getManageLink: (id: string) => `https://console.hetzner.cloud/projects/${id}/servers`,
    getConsoleLink: () => undefined,
    summary: ["High performance", "Low cost", "Huge bandwith allocation"]
  },
  datacrunch: {
    id: "datacrunch",
    order: 3,
    name: "Verda Cloud",
    description: "formerly DataCrunch",
    logoUrl: "/assets/provider/verda-logo.svg",
    authType: "accessKeySecretKey",
    accessKeyName: "Client ID",
    secretKeyName: "Client Secret",
    compute: true,
    consoleUrl: "https://console.verda.com/",
    tokenHelpUrl: "https://docs.verda.com/",
    getManageLink: (_id: string) => undefined,
    getConsoleLink: (_id: string) => undefined,
    summary: ["GPU instances", "High performance", "AI/ML optimized"]
  },
  aws: {
    id: "aws",
    order: 4,
    name: "AWS",
    authType: "accessKeySecretKey",
    logoUrl: "/assets/provider/aws-logo.svg",
    consoleUrl: "https://console.aws.amazon.com/",
    experimental: true,
    compute: true,
    getManageLink: (_id: string) => undefined,
    getConsoleLink: (_id: string) => undefined,
    summary: ["Flexible", "Reliable and secure", "Highly scalable"]
  },
  backup: {
    id: "backup",
    order: 5,
    name: "Backup Storage",
    authType: "accessKeySecretKey",
    logoUrl: "https://avatars.githubusercontent.com/u/2232217",
    consoleUrl: "https://console.aws.amazon.com/",
    experimental: false,
    getManageLink: (_id: string) => undefined,
    getConsoleLink: (_id: string) => undefined,
    summary: ["S3-compatible storage", "Secure backups", "Flexible configuration"]
  },
  github: {
    id: "github",
    order: 6,
    name: "GitHub",
    authType: "oauth",
    compute: false,
    logoUrl: "https://avatars.githubusercontent.com/u/9919",
    consoleUrl: "https://github.com/settings/installations",
    getManageLink: (_id: string) => undefined,
    getConsoleLink: (_id: string) => undefined,
    summary: ["GitHub Actions", "GitHub Apps", "GitHub API"]
  }
};

export const getProviderMeta = (id: string) =>
  id in providerMeta ? providerMeta[id as ProviderType] : undefined;

export const getProviders = () =>
  Object.values(providerMeta).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
